import axios from 'axios';
import type { ExcelRow } from '../excel/types';

interface Page<T> {
  items: T[];
  totalCount: number;
}

async function fetchPageRecursive<T>(
  fetchPage: (offset: number, limit: number) => Promise<Page<T>>,
  pageSize: number,
  offset: number,
  collected: T[],
): Promise<T[]> {
  const page = await fetchPage(offset, pageSize);
  const accumulated = [...collected, ...page.items];

  if (accumulated.length >= page.totalCount) {
    return accumulated;
  }

  return fetchPageRecursive(fetchPage, pageSize, offset + pageSize, accumulated);
}

export async function fetchAllPages<T>(
  fetchPage: (offset: number, limit: number) => Promise<Page<T>>,
  pageSize: number,
): Promise<T[]> {
  return fetchPageRecursive(fetchPage, pageSize, 0, []);
}

export function readString(row: ExcelRow, key: string): string | undefined {
  const value = row[key];

  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if (typeof record.hyperlink === 'string') {
      return record.hyperlink.replace(/^mailto:/i, '');
    }

    if (typeof record.text === 'string') {
      return record.text;
    }
  }

  return String(value);
}

export function readBoolean(row: ExcelRow, key: string): boolean | undefined {
  const value = row[key];

  if (typeof value === 'boolean') {
    return value;
  }

  if (value === undefined || value === null) {
    return undefined;
  }

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

export async function runTasksSequentially<T>(
  tasks: (() => Promise<T>)[],
  onError: (reason: unknown) => void,
): Promise<void> {
  await tasks.reduce(async (previousTask, task) => {
    await previousTask;

    try {
      await task();
    } catch (reason) {
      onError(reason);
    }
  }, Promise.resolve());
}

export function extractErrorPayload(reason: any): any {
  if (axios.isAxiosError(reason)) {
    return reason.response?.data;
  }

  return reason;
}

export function isAllegroUnauthorized(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

export async function swallowUnauthorized<T>(
  fun: () => Promise<T>,
  fallback: T,
): Promise<T> {
  try {
    return await fun();
  } catch (error) {
    if (isAllegroUnauthorized(error)) {
      return fallback;
    }

    throw error;
  }
}
